import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SEO } from '../../components/SEO'
import { BackButton } from '../../components/BackButton'
import { insignias } from '../../constants/insignias'
import './SotilasarvotPage.scss'
import { ARMY_NUMBERS, NAVY_NUMBERS, mapInsigniaToRank } from './ranks'

interface Option {
  value: number
}

const getRandomInt = (min: number, max: number): number => {
  const low = Math.ceil(min)
  const high = Math.floor(max)
  return Math.floor(Math.random() * (high - low + 1)) + low
}

const pickRound = (pool: number[]) => {
  const correctIndex = getRandomInt(0, pool.length - 1)
  const correctValue = pool[correctIndex]

  // Ensure wrong options come from the same branch as the correct one
  const isArmy = ARMY_NUMBERS.includes(correctValue)
  const branchPoolSet = new Set<number>(isArmy ? ARMY_NUMBERS : NAVY_NUMBERS)
  const wrongPool = pool.filter((n) => branchPoolSet.has(n) && n !== correctValue)

  const wrongValues = new Set<number>()
  while (wrongValues.size < 3 && wrongPool.length > 0) {
    const candidate = wrongPool[getRandomInt(0, wrongPool.length - 1)]
    if (candidate !== correctValue) {
      wrongValues.add(candidate)
    }
  }

  const options: Option[] = [
    { value: correctValue },
    ...Array.from(wrongValues).map((v) => ({ value: v })),
  ]

  // Shuffle options in-place
  for (let i = options.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    const tmp = options[i]
    options[i] = options[j]
    options[j] = tmp
  }

  return { correctValue, options }
}

export const SotilasarvotPage: React.FC = () => {
  const [includeArmy, setIncludeArmy] = useState(true)
  const [includeNavy, setIncludeNavy] = useState(true)
  const ARMY_KEY = 'sotilasarvot_includeArmy'
  const NAVY_KEY = 'sotilasarvot_includeNavy'

  // Load persisted preferences once
  useEffect(() => {
    try {
      const a = localStorage.getItem(ARMY_KEY)
      const n = localStorage.getItem(NAVY_KEY)
      if (a !== null || n !== null) {
        const nextA = a === null ? includeArmy : a === '1'
        const nextN = n === null ? includeNavy : n === '1'
        if (!nextA && !nextN) {
          setIncludeArmy(true)
          setIncludeNavy(false)
        } else {
          setIncludeArmy(nextA)
          setIncludeNavy(nextN)
        }
      }
    } catch (_e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const pool = useMemo(() => {
    const list: number[] = []
    if (includeArmy) list.push(...ARMY_NUMBERS)
    if (includeNavy) list.push(...NAVY_NUMBERS)
    return list.length > 0 ? list : ARMY_NUMBERS
  }, [includeArmy, includeNavy])
  const [score, setScore] = useState(0)
  const [round, setRound] = useState(() => pickRound([...ARMY_NUMBERS, ...NAVY_NUMBERS]))
  const [selected, setSelected] = useState<number | null>(null)

  useEffect(() => {
    setRound(pickRound(pool))
  }, [pool])

  // Persist preferences when changed
  useEffect(() => {
    try {
      localStorage.setItem(ARMY_KEY, includeArmy ? '1' : '0')
      localStorage.setItem(NAVY_KEY, includeNavy ? '1' : '0')
    } catch (_e) {
      // ignore
    }
  }, [includeArmy, includeNavy])

  const imageSrc = useMemo(() => insignias[round.correctValue - 1], [round.correctValue])

  const submitAnswer = useCallback(
    (value: number) => {
      if (selected !== null) return
      setSelected(value)

      const isCorrect = value === round.correctValue
      if (isCorrect) {
        setScore((s) => s + 1)
      }

      window.setTimeout(() => {
        setRound(pickRound(pool))
        setSelected(null)
      }, 900)
    },
    [round.correctValue, selected, pool]
  )

  return (
    <div className="sotilasarvotPage">
      <SEO
        title="Sotilasarvot | Aamukasaaja"
        description="Vältä puhutteluvirheet ja opettele sotilasarvot Aamukasaajasta."
        keywords="aamukasa, tj, intti, sotilasarvot, arvomerkki, inttipeli"
        canonical="https://aamukasaaja.fi/sotilasarvot"
      />

      <div className="header">
        <BackButton to="/" />
        <h2>Sotilasarvot</h2>
      </div>

      <div className="quizContainer" role="group" aria-label="Sotilasarvot-visa">
        <div className="prompt">
          <img src={imageSrc} alt="Arvomerkki" className="insignia" />
          <p className="question">Mikä arvomerkki kuvassa on?</p>
        </div>

        <div className="options">
          {round.options.map((opt) => {
            const isSelected = selected === opt.value
            const isCorrect = opt.value === round.correctValue
            const className = ['optionButton', isSelected && (isCorrect ? 'correct' : 'wrong')]
              .filter(Boolean)
              .join(' ')
            return (
              <button
                key={opt.value}
                type="button"
                className={className}
                onClick={() => submitAnswer(opt.value)}
                disabled={selected !== null}
                aria-pressed={isSelected}
              >
                {mapInsigniaToRank(opt.value)}
              </button>
            )
          })}
        </div>

        <div className="score" aria-live="polite">
          Oikein vastatut: {score}
        </div>

        <div className="branchToggles" role="group" aria-label="Haaravalinnat">
          <label className="branchToggle">
            <input
              type="checkbox"
              checked={includeArmy}
              className="branchCheckbox"
              onChange={() => {
                if (!includeNavy && includeArmy) return
                setIncludeArmy((v) => !v)
              }}
            />
            Maavoimat
          </label>
          <label className="branchToggle">
            <input
              type="checkbox"
              checked={includeNavy}
              className="branchCheckbox"
              onChange={() => {
                if (!includeArmy && includeNavy) return
                setIncludeNavy((v) => !v)
              }}
            />
            Merivoimat
          </label>
        </div>
      </div>
    </div>
  )
}

export default SotilasarvotPage
